/**
 * O cartão das Formas de pagamento (13.22a2, FR-92) — Configuração → Negócios.
 *
 * A lista mora no `negocios` e tem o molde da Previsão de fechamento (`PrevisaoDeFechamento.tsx`):
 * **rótulo**, **descrição** (o texto que a foto do orçamento copia) e **taxa** opcional, em
 * percentual. Numa instalação nova ela nasce com as três do POP de Vendas — Boleto, Cartão
 * PagSeguro (6%) e Faturado. Toda forma com taxa gera o "total no cartão" do orçamento.
 *
 * **Excluir é lógico e em dois passos**: a forma some do select de "Gerar orçamento", e as fotos que
 * já a usaram continuam dizendo o que foi oferecido (elas guardam a cópia). Cadastrar de novo um
 * rótulo excluído o **reativa**, e a tela diz isso.
 *
 * **Tudo o que o serviço recusar aparece verbatim** — o `403` de quem não tem
 * `administrar_listas_previsao_e_campos`, o `409` do rótulo repetido, o `422` da taxa. Esconder
 * botão por permissão seria conveniência, e não autorização; quem recusa é o serviço.
 */

import { type FormEvent, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import '@/modulos/cadastro/listas-de-dominio.css'
import {
  type FormaDePagamento,
  useCadastrarFormaDePagamento,
  useEditarFormaDePagamento,
  useExcluirFormaDePagamento,
  useFormasDePagamento,
} from './api'
import { textoDaTaxa } from './OrcamentoDoCrm'
import './previsao-de-fechamento.css'
import './formas-de-pagamento.css'

/** Os tetos do serviço (`TAMANHO_DO_ROTULO_DA_FORMA`, `TAMANHO_DA_DESCRICAO_DA_FORMA`). */
export const TAMANHO_DO_ROTULO_DA_FORMA = 80
export const TAMANHO_DA_DESCRICAO_DA_FORMA = 500

export const FALTA_ROTULO_DA_FORMA = 'Informe o rótulo da forma de pagamento.'
export const TAXA_INVALIDA = 'Informe a taxa em percentual, de 0 a 100, com até duas casas.'
export const FORMA_CADASTRADA = 'Forma de pagamento cadastrada.'
export const FORMA_RESTAURADA = 'A forma existia excluída e voltou para a lista.'
export const FORMA_SALVA = 'Forma de pagamento salva.'
export const FORMA_EXCLUIDA = 'Forma excluída. Os orçamentos que já a usaram continuam com ela.'

const ID_DO_TITULO = 'formas-de-pagamento-titulo'

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * A taxa digitada, no formato do contrato (`"6.00"` com ponto), ou `null` quando vazia. Devolve
 * `undefined` quando o texto não é um percentual de 0 a 100 com até duas casas — a recusa que
 * evita a ida à rede com um `422` genérico.
 */
export function taxaDoCampo(texto: string): string | null | undefined {
  const limpo = texto.trim().replace(',', '.')
  if (limpo === '') return null
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(limpo)) return undefined
  const numero = Number(limpo)
  if (numero > 100) return undefined
  return limpo
}

/** A taxa gravada, no campo: `"6.00"` vira `"6,00"`; sem taxa, vazio. */
function taxaNoCampo(taxa: string | null): string {
  return taxa === null ? '' : taxa.replace('.', ',')
}

interface Edicao {
  rotulo: string
  descricao: string
  taxa: string
  aoConcluir: () => void
}

function CamposDaForma({
  prefixo,
  rotulo,
  descricao,
  taxa,
  aoTrocarRotulo,
  aoTrocarDescricao,
  aoTrocarTaxa,
  rotuloDoNome,
}: {
  prefixo: string
  rotulo: string
  descricao: string
  taxa: string
  aoTrocarRotulo: (valor: string) => void
  aoTrocarDescricao: (valor: string) => void
  aoTrocarTaxa: (valor: string) => void
  rotuloDoNome: string
}) {
  return (
    <>
      <div className="listas__campo">
        <label className="listas__rotulo" htmlFor={`${prefixo}-rotulo`}>
          {rotuloDoNome}
        </label>
        <input
          id={`${prefixo}-rotulo`}
          className="listas__entrada"
          value={rotulo}
          maxLength={TAMANHO_DO_ROTULO_DA_FORMA}
          onChange={(evento) => {
            aoTrocarRotulo(evento.target.value)
          }}
        />
      </div>
      <div className="listas__campo">
        <label className="listas__rotulo" htmlFor={`${prefixo}-descricao`}>
          Descrição — vai no orçamento
        </label>
        <input
          id={`${prefixo}-descricao`}
          className="listas__entrada"
          value={descricao}
          maxLength={TAMANHO_DA_DESCRICAO_DA_FORMA}
          onChange={(evento) => {
            aoTrocarDescricao(evento.target.value)
          }}
        />
      </div>
      <div className="listas__campo">
        <label className="listas__rotulo" htmlFor={`${prefixo}-taxa`}>
          Taxa (%) — opcional
        </label>
        <input
          id={`${prefixo}-taxa`}
          className="listas__entrada"
          inputMode="decimal"
          value={taxa}
          maxLength={6}
          onChange={(evento) => {
            aoTrocarTaxa(evento.target.value)
          }}
        />
      </div>
    </>
  )
}

function LinhaDaForma({
  forma,
  ocupado,
  aoSalvar,
  aoExcluir,
}: {
  forma: FormaDePagamento
  ocupado: boolean
  aoSalvar: (forma: FormaDePagamento, edicao: Edicao) => void
  aoExcluir: (forma: FormaDePagamento) => void
}) {
  const [editando, setEditando] = useState(false)
  const [confirmando, setConfirmando] = useState(false)
  const [rotulo, setRotulo] = useState(forma.rotulo)
  const [descricao, setDescricao] = useState(forma.descricao ?? '')
  const [taxa, setTaxa] = useState(taxaNoCampo(forma.taxa_percentual))

  function abrirEdicao() {
    setRotulo(forma.rotulo)
    setDescricao(forma.descricao ?? '')
    setTaxa(taxaNoCampo(forma.taxa_percentual))
    setEditando(true)
  }

  if (editando) {
    return (
      <li className="listas__item">
        <form
          className="listas__formulario previsao__formulario"
          onSubmit={(evento) => {
            evento.preventDefault()
            aoSalvar(forma, {
              rotulo,
              descricao,
              taxa,
              aoConcluir: () => {
                setEditando(false)
              },
            })
          }}
        >
          <CamposDaForma
            prefixo={`forma-${forma.id}`}
            rotulo={rotulo}
            descricao={descricao}
            taxa={taxa}
            aoTrocarRotulo={setRotulo}
            aoTrocarDescricao={setDescricao}
            aoTrocarTaxa={setTaxa}
            rotuloDoNome="Rótulo"
          />
          <div className="listas__acoes">
            <button type="submit" className="listas__botao" disabled={ocupado}>
              Salvar
            </button>
            <button
              type="button"
              className="listas__botao listas__botao--discreto"
              onClick={() => {
                setEditando(false)
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      </li>
    )
  }

  return (
    <li className="listas__item">
      {/* Code review da 13.22 (F21): o rótulo, a taxa e a descrição num bloco só — a descrição
          logo abaixo do rótulo —, e os botões no fim da linha (a 375, embaixo, à direita). */}
      <div className="listas__linha formas__linha">
        <div className="formas__texto">
          <span className="listas__valor">{forma.rotulo}</span>
          <span className="listas__status">
            {forma.taxa_percentual === null
              ? 'Sem taxa'
              : `Taxa de ${textoDaTaxa(forma.taxa_percentual)}`}
          </span>
          {forma.descricao === null ? null : <p className="listas__ajuda">{forma.descricao}</p>}
        </div>
        <div className="listas__acoes formas__acoes">
          {confirmando ? (
            <>
              <button
                type="button"
                className="listas__botao listas__botao--perigo"
                disabled={ocupado}
                onClick={() => {
                  aoExcluir(forma)
                  setConfirmando(false)
                }}
              >
                Confirmar exclusão
              </button>
              <button
                type="button"
                className="listas__botao listas__botao--discreto"
                onClick={() => {
                  setConfirmando(false)
                }}
              >
                Cancelar
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="listas__botao listas__botao--discreto"
                aria-label={`Editar ${forma.rotulo}`}
                onClick={abrirEdicao}
              >
                Editar
              </button>
              <button
                type="button"
                className="listas__botao listas__botao--discreto"
                aria-label={`Excluir ${forma.rotulo}`}
                onClick={() => {
                  setConfirmando(true)
                }}
              >
                Excluir
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  )
}

export function FormasDePagamento() {
  const consulta = useFormasDePagamento()
  const cadastro = useCadastrarFormaDePagamento()
  const edicao = useEditarFormaDePagamento()
  const exclusao = useExcluirFormaDePagamento()

  const [rotulo, setRotulo] = useState('')
  const [descricao, setDescricao] = useState('')
  const [taxa, setTaxa] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [confirmacao, setConfirmacao] = useState<string | null>(null)

  const ocupado = cadastro.isPending || edicao.isPending || exclusao.isPending

  function limpar() {
    setErro(null)
    setConfirmacao(null)
  }

  function aoFalhar(falha: unknown) {
    setConfirmacao(null)
    setErro(mensagemDe(falha))
  }

  /** O corpo do serviço, ou a frase da tela quando o que foi digitado não chega a ser corpo. */
  function corpoDe(valores: { rotulo: string; descricao: string; taxa: string }) {
    if (valores.rotulo.trim() === '') return FALTA_ROTULO_DA_FORMA
    const taxaDoCorpo = taxaDoCampo(valores.taxa)
    if (taxaDoCorpo === undefined) return TAXA_INVALIDA
    return {
      rotulo: valores.rotulo.trim(),
      descricao: valores.descricao.trim() === '' ? null : valores.descricao.trim(),
      taxa_percentual: taxaDoCorpo,
    }
  }

  function cadastrar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    limpar()
    const corpo = corpoDe({ rotulo, descricao, taxa })
    if (typeof corpo === 'string') {
      setErro(corpo)
      return
    }
    cadastro.mutate(corpo, {
      onSuccess: ({ restaurada }) => {
        setConfirmacao(restaurada ? FORMA_RESTAURADA : FORMA_CADASTRADA)
        setRotulo('')
        setDescricao('')
        setTaxa('')
      },
      onError: aoFalhar,
    })
  }

  return (
    <section className="listas" aria-labelledby={ID_DO_TITULO}>
      <div className="listas__cartao">
        <div className="listas__cabecalho">
          <h2 className="listas__titulo" id={ID_DO_TITULO}>
            Formas de pagamento
          </h2>
          <span className="listas__ajuda">
            As formas que o vendedor escolhe ao gerar o orçamento. A descrição vai no orçamento, e a
            forma com taxa mostra também o total no cartão.
          </span>
        </div>
        <div className="listas__corpo">
          {consulta.isError ? (
            <p className="listas__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : consulta.isPending ? (
            <p className="listas__ajuda" role="status">
              Carregando as formas de pagamento…
            </p>
          ) : consulta.data.length === 0 ? (
            <p className="listas__ajuda">Nenhuma forma de pagamento. Cadastre a primeira abaixo.</p>
          ) : (
            <ul className="listas__lista" aria-label="Formas de pagamento">
              {consulta.data.map((forma) => (
                <LinhaDaForma
                  key={forma.id}
                  forma={forma}
                  ocupado={ocupado}
                  aoSalvar={(atual, nova) => {
                    limpar()
                    const corpo = corpoDe(nova)
                    if (typeof corpo === 'string') {
                      setErro(corpo)
                      return
                    }
                    edicao.mutate(
                      { formaId: atual.id, edicao: corpo },
                      {
                        onSuccess: () => {
                          nova.aoConcluir()
                          setConfirmacao(FORMA_SALVA)
                        },
                        onError: aoFalhar,
                      },
                    )
                  }}
                  aoExcluir={(atual) => {
                    limpar()
                    exclusao.mutate(atual.id, {
                      onSuccess: () => {
                        setConfirmacao(FORMA_EXCLUIDA)
                      },
                      onError: aoFalhar,
                    })
                  }}
                />
              ))}
            </ul>
          )}

          <form className="listas__formulario" onSubmit={cadastrar}>
            <CamposDaForma
              prefixo="forma-nova"
              rotulo={rotulo}
              descricao={descricao}
              taxa={taxa}
              aoTrocarRotulo={setRotulo}
              aoTrocarDescricao={setDescricao}
              aoTrocarTaxa={setTaxa}
              rotuloDoNome="Nova forma"
            />
            <div className="listas__acoes">
              <button type="submit" className="listas__botao" disabled={ocupado}>
                Cadastrar forma
              </button>
            </div>
          </form>

          {erro === null ? null : (
            <p className="listas__erro" role="alert">
              {erro}
            </p>
          )}
          {confirmacao === null || erro !== null ? null : (
            <p className="listas__sucesso" role="status">
              {confirmacao}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
